import React from 'react';
import { Card, CardContent, Box, Typography, useTheme } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { glassStyle } from '../../theme/luxuryTheme';

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  delay?: number;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  icon,
  trend = 'neutral',
  delay = 0,
}) => {
  const theme = useTheme();

  const getTrendColor = () => {
    if (trend === 'up') return '#4caf50';
    if (trend === 'down') return '#f44336';
    return theme.palette.text.secondary;
  };

  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp sx={{ fontSize: 16 }} />;
    if (trend === 'down') return <TrendingDown sx={{ fontSize: 16 }} />;
    return null;
  };

  // Extract numeric value for CountUp
  const numericValue = typeof value === 'string'
    ? parseFloat(value.replace(/[^0-9.-]+/g, ''))
    : value;

  const prefix = typeof value === 'string' && value.includes('$') ? '$' : '';
  const decimals = numericValue % 1 !== 0 ? 2 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ scale: 1.03 }}
    >
      <Card
        sx={{
          ...glassStyle,
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 12px 40px rgba(175, 35, 38, 0.3)',
            border: '1px solid rgba(175, 35, 38, 0.3)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, transparent, rgba(175, 35, 38, 0.6), transparent)',
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.75rem',
              }}
            >
              {title}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(175, 35, 38, 0.2), rgba(175, 35, 38, 0.1))',
                color: 'primary.main',
              }}
            >
              {icon}
            </Box>
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              background: 'linear-gradient(135deg, #ffffff 0%, #b8b8b8 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {prefix}
            <CountUp
              end={numericValue}
              duration={1.5}
              decimals={decimals}
              separator=","
              preserveValue
            />
          </Typography>

          {change !== undefined && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {getTrendIcon()}
              <Typography
                variant="body2"
                sx={{
                  color: getTrendColor(),
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {change > 0 ? '+' : ''}{change.toFixed(1)}%
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  ml: 0.5,
                }}
              >
                vs last period
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default MetricCard;
